"use client";

// @ubm/ui/components/time-grid — LE noyau de grille temps maison (L0, promotion signée Root 06/10/2026). Extrait
// d'AgendaTimeGrid (@ubm/booking-ui) : même présentation (en-têtes, gouttière, colonnes, filets d'heure, fantôme de
// sélection, ligne « maintenant », cadre d'évènement positionné) et MÊME contrôleur géométrique (glisser souris sur une
// zone vide ; au toucher, appui long immobile puis glisser ; garde des événements souris de compatibilité ; défilement natif
// bloqué seulement pendant une sélection tactile active). Deux appelants : l'agenda Booking et le bloc WeekCalendar.
//
// Le noyau ne connaît NI date NI fuseau NI métier : il reçoit des colonnes (clé, libellé, en-tête, contenu positionné),
// des graduations, une fenêtre en minutes, et rend des INTENTIONS géométriques (plage sélectionnée, évènement déplacé ou
// étiré) exprimées en minutes depuis le début de la fenêtre. Le parent convertit, persiste, gère pending et refus ; aucun
// succès n'est simulé ici. Extensions additives (WeekCalendar) : snap configurable, déplacement/redimensionnement
// d'évènements marqués `movable`, rangée « journée entière », cellules enrichies (clavier, libellés).
import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type HTMLAttributes,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import { resizeRange, shiftRange, snapRange, timeGridPercent, yToMinutes } from "./geometry";
import styles from "./time-grid.module.css";

/** Appui long tactile qui ouvre une sélection (convention Calendrier iOS) — assez long pour ne pas voler un tap. */
export const TIME_GRID_LONG_PRESS_MS = 450;
/** Déplacement (px) au-delà duquel un appui en attente devient un défilement (annulé). */
export const TIME_GRID_LONG_PRESS_SLOP_PX = 10;
/** Après la FIN d'un toucher (pointerup/cancel), délai pendant lequel les événements souris rejoués sont ignorés. */
export const TIME_GRID_TOUCH_COMPAT_MS = 800;
/** Distance (px) au-delà de laquelle un appui souris sur un évènement devient un glisser (en deçà : un clic). */
const EVENT_DRAG_THRESHOLD_PX = 4;

type DataAttributes = { [key: `data-${string}`]: string | number | boolean | undefined };

export interface TimeGridColumn {
  key: string;
  /** Libellé accessible de la cellule (aria-label). */
  label: string;
  /** En-tête rendu en tête de colonne. */
  header: ReactNode;
  /** Contenu positionné de la colonne (évènements, marqueurs). */
  children?: ReactNode;
  /** Cellule « journée entière » de la colonne (rangée affichée si `allDay` est fourni). */
  allDay?: ReactNode;
  /** Attributs supplémentaires de la cellule (tabIndex, aria-*, data-*, onKeyDown…), composés avec le contrôleur. */
  cellProps?: HTMLAttributes<HTMLDivElement> & DataAttributes;
}

export interface TimeGridGraduation {
  key: string | number;
  label: ReactNode;
}

/** Plage tirée sur une zone vide, en minutes depuis le début de la fenêtre. */
export interface TimeGridRange {
  columnKey: string;
  columnIndex: number;
  startMinute: number;
  endMinute: number;
}

/** Évènement déplacé (corps) ou étiré (poignée de fin), proposé au parent ; rien n'est appliqué par la grille. */
export interface TimeGridEventIntent {
  kind: "move" | "resize";
  id: string;
  fromColumnKey: string;
  columnKey: string;
  columnIndex: number;
  startMinute: number;
  endMinute: number;
}

export interface TimeGridNow {
  /** Minutes depuis le début de la fenêtre. Hors [0, fenêtre] : aucune ligne. */
  minute: number;
  /** Colonnes qui portent la ligne. */
  columnKeys: readonly string[];
  label?: string;
  /** Colonne dont la ligne porte la pastille (posée dans la gouttière : la première colonne en pratique). */
  labelColumnKey?: string;
}

export interface TimeGridClassNames {
  root?: string;
  head?: string;
  corner?: string;
  columnHeader?: string;
  allDay?: string;
  allDayLabel?: string;
  allDayCell?: string;
  body?: string;
  gutter?: string;
  graduation?: string;
  column?: string;
  hourLine?: string;
  selection?: string;
  preview?: string;
}

export interface TimeGridNowRenderProps {
  style: CSSProperties;
  label?: string;
}

export interface TimeGridProps extends Omit<HTMLAttributes<HTMLDivElement>, "children" | "onSelect"> {
  columns: readonly TimeGridColumn[];
  /** Graduations de la gouttière (une par rangée : les heures en pratique). */
  graduations: readonly TimeGridGraduation[];
  /** Durée de la fenêtre affichée, en minutes. */
  windowMinutes: number;
  /** Pas d'aimantation des sélections et déplacements. Défaut 30 (agenda Booking). */
  snapMinutes?: number;
  /** Zone vide → plage snappée. Absent : grille non sélectionnable. */
  onSelectRange?: (range: TimeGridRange) => void;
  /** Déplacement/étirement d'un évènement `movable`. Absent : évènements fixes. */
  onEventChange?: (intent: TimeGridEventIntent) => void;
  /** Glisser en cours sur un évènement (ou fin : null) — pour annoncer ou styliser côté parent. */
  onEventDragChange?: (intent: TimeGridEventIntent | null) => void;
  now?: TimeGridNow | null;
  /** Rendu de la ligne « maintenant » (défaut : TimeGridNowLine). */
  renderNow?: (props: TimeGridNowRenderProps) => ReactNode;
  /**
   * Rangée « journée entière » : libellé de gouttière, puis SOIT une cellule par colonne (`columns[].allDay`), SOIT un
   * `content` unique qui couvre toutes les colonnes (barres multi-jours sans doublon ; `--tg-columns` pour sa grille).
   */
  allDay?: { label?: ReactNode; content?: ReactNode; className?: string } | null;
  /** Durée de l'appui long tactile. Défaut 450 ms. */
  longPressMs?: number;
  /** Échap annule une sélection en cours (extension WeekCalendar). Défaut false : comportement Booking d'origine. */
  escapeCancels?: boolean;
  /** Description accessible quand la grille est sélectionnable. */
  selectDescription?: string;
  /** Crochets publics additionnels (classes des appelants), posés à côté des classes du noyau. */
  classNames?: TimeGridClassNames;
}

interface DragState {
  colIndex: number;
  y0: number;
  y1: number;
  height: number;
  top: number;
  /** Sélection TACTILE active (pointeur capturé) ; absent = sélection souris. */
  pointerId?: number;
}

interface PendingPress {
  pointerId: number;
  colIndex: number;
  x: number;
  y: number;
  height: number;
  top: number;
  cell: HTMLElement;
  timer: ReturnType<typeof setTimeout>;
  /** Appui sur un évènement déplaçable : l'appui long démarre son déplacement. */
  event?: EventGrab;
}

interface EventGrab {
  id: string;
  kind: "move" | "resize";
  colIndex: number;
  startMinute: number;
  endMinute: number;
}

interface EventDrag extends EventGrab {
  x0: number;
  y0: number;
  height: number;
  rects: { left: number; right: number }[];
  started: boolean;
  /** La prévisualisation a été publiée au moins une fois. */
  shown: boolean;
  pointerId?: number;
  current: TimeGridEventIntent;
}

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" ");

/** Compose deux gestionnaires d'événement : celui de l'appelant d'abord, puis celui du contrôleur. */
function chain<E extends SyntheticEvent>(a?: (event: E) => void, b?: (event: E) => void) {
  if (!a) return b;
  if (!b) return a;
  return (event: E) => { a(event); b(event); };
}

function readGrab(target: EventTarget | null): EventGrab | null {
  const el = (target as Element | null)?.closest?.("[data-time-grid-event]") as HTMLElement | null;
  if (!el || el.dataset.tgMovable !== "true") return null;
  const id = el.dataset.tgId;
  const start = Number(el.dataset.tgStart);
  const end = Number(el.dataset.tgEnd);
  const colIndex = Number((el.closest("[data-tg-col]") as HTMLElement | null)?.dataset.tgCol);
  if (!id || !Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(colIndex)) return null;
  const onHandle = !!(target as Element).closest?.("[data-time-grid-handle]");
  return { id, kind: onHandle ? "resize" : "move", colIndex, startMinute: start, endMinute: end };
}

export const TimeGrid = forwardRef<HTMLDivElement, TimeGridProps>(function TimeGrid(
  {
    columns,
    graduations,
    windowMinutes,
    snapMinutes = 30,
    onSelectRange,
    onEventChange,
    onEventDragChange,
    now,
    renderNow,
    allDay,
    longPressMs = TIME_GRID_LONG_PRESS_MS,
    escapeCancels = false,
    selectDescription = "To choose a range, drag over free time with a mouse, or press and hold then drag on touch.",
    classNames = {},
    className,
    style,
    ...rest
  },
  forwardedRef,
) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const setDragBoth = useCallback((d: DragState | null) => {
    dragRef.current = d;
    setDrag(d);
  }, []);
  const [preview, setPreview] = useState<TimeGridEventIntent | null>(null);
  const eventDragRef = useRef<EventDrag | null>(null);
  const pendingRef = useRef<PendingPress | null>(null);
  /** Un toucher/stylet est en cours (de pointerdown à pointerup/pointercancel), quelle que soit sa durée. */
  const touchDown = useRef(false);
  /** Instant de FIN du dernier toucher (pointerup/pointercancel) — point de départ de la garde de compatibilité. */
  const lastTouchEnd = useRef(0);
  /** Un glisser d'évènement vient de se terminer : le clic qui suit n'ouvre pas l'évènement. */
  const swallowClick = useRef(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const setRoot = (el: HTMLDivElement | null) => {
    rootRef.current = el;
    if (typeof forwardedRef === "function") forwardedRef(el);
    else if (forwardedRef) forwardedRef.current = el;
  };
  const interactive = !!onSelectRange || !!onEventChange;
  const latest = useRef({ columns, windowMinutes, snapMinutes, onEventChange, onEventDragChange });
  latest.current = { columns, windowMinutes, snapMinutes, onEventChange, onEventDragChange };

  const clearPending = useCallback(() => {
    const p = pendingRef.current;
    if (p) clearTimeout(p.timer);
    pendingRef.current = null;
  }, []);

  /**
   * Garde des événements souris « de compatibilité » (rejoués par le navigateur APRÈS un toucher) : fermée pendant
   * TOUTE la séquence tactile, puis TIME_GRID_TOUCH_COMPAT_MS après sa fin — jamais mesurée depuis pointerdown, sinon un
   * appui long suivi d'un glisser plus long laisserait passer un mousedown/mouseup rejoué (seconde plage).
   */
  const isCompatMouse = () => touchDown.current || Date.now() - lastTouchEnd.current < TIME_GRID_TOUCH_COMPAT_MS;

  // ---- Glisser d'évènement (déplacement / étirement) ----
  const intentAt = useCallback((d: EventDrag, x: number, y: number): TimeGridEventIntent => {
    const { columns: cols, windowMinutes: win, snapMinutes: snap } = latest.current;
    const delta = ((y - d.y0) / d.height) * win;
    let colIndex = d.colIndex;
    if (d.kind === "move") {
      const hit = d.rects.findIndex(r => x >= r.left && x < r.right);
      if (hit >= 0) colIndex = hit;
    }
    const next = d.kind === "move" ? shiftRange(d.startMinute, d.endMinute, delta, snap, win) : resizeRange(d.startMinute, d.endMinute, delta, snap, win);
    return { kind: d.kind, id: d.id, fromColumnKey: cols[d.colIndex]?.key ?? "", columnKey: cols[colIndex]?.key ?? "", columnIndex: colIndex, ...next };
  }, []);

  const beginEventDrag = useCallback((grab: EventGrab, x: number, y: number, pointerId?: number, started = false) => {
    const root = rootRef.current;
    if (!root) return;
    const cells = Array.from(root.querySelectorAll<HTMLElement>("[data-tg-col]"));
    const rects = cells.map(c => { const r = c.getBoundingClientRect(); return { left: r.left, right: r.right }; });
    const height = cells[grab.colIndex]?.getBoundingClientRect().height || 1;
    const cols = latest.current.columns;
    const current: TimeGridEventIntent = { kind: grab.kind, id: grab.id, fromColumnKey: cols[grab.colIndex]?.key ?? "", columnKey: cols[grab.colIndex]?.key ?? "", columnIndex: grab.colIndex, startMinute: grab.startMinute, endMinute: grab.endMinute };
    eventDragRef.current = { ...grab, x0: x, y0: y, height, rects, started, shown: started, pointerId, current };
    if (started) { setPreview(current); latest.current.onEventDragChange?.(current); }
  }, []);

  const moveEventDrag = useCallback((x: number, y: number) => {
    const d = eventDragRef.current;
    if (!d) return;
    if (!d.started) {
      if (Math.hypot(x - d.x0, y - d.y0) < EVENT_DRAG_THRESHOLD_PX) return;
      d.started = true;
    }
    const next = intentAt(d, x, y);
    const same = next.columnIndex === d.current.columnIndex && next.startMinute === d.current.startMinute && next.endMinute === d.current.endMinute;
    d.current = next;
    if (!same || !d.shown) { d.shown = true; setPreview(next); latest.current.onEventDragChange?.(next); }
  }, [intentAt]);

  const endEventDrag = useCallback((commit: boolean) => {
    const d = eventDragRef.current;
    eventDragRef.current = null;
    setPreview(null);
    if (!d) return;
    if (d.started) {
      swallowClick.current = true;
      setTimeout(() => { swallowClick.current = false; }, 0);
      latest.current.onEventDragChange?.(null);
      const c = d.current;
      const changed = c.columnIndex !== d.colIndex || c.startMinute !== d.startMinute || c.endMinute !== d.endMinute;
      if (commit && changed) latest.current.onEventChange?.(c);
    }
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !interactive) return;
    // ARBITRAGE DÉFILEMENT / SÉLECTION, explicite et dans cet ordre :
    //   1. pendant l'attente de l'appui long, rien n'est bloqué : un doigt qui bouge (> slop) est un défilement, l'appui
    //      est annulé (onPointerMove) — et si le navigateur prend le geste, pointercancel aussi ;
    //   2. une fois l'appui long abouti, le défilement natif est annulé par `preventDefault()` sur `touchmove` (Touch
    //      Events, W3C). Listener NON passif posé à la main : React délègue ses touch handlers en passif.
    // Pourquoi pas `touch-action` : le navigateur le fige au DÉBUT du geste (Pointer Events, W3C) ; `touch-action: none`
    // supprimerait tout défilement au doigt (cadre horizontal compris) : refusé. Filet : pointercancel abandonne le geste.
    const onTouchMove = (e: TouchEvent) => {
      const active = dragRef.current?.pointerId !== undefined || eventDragRef.current?.pointerId !== undefined;
      if (active && e.cancelable) e.preventDefault();
    };
    // Fin d'un toucher OÙ QU'ELLE SE PRODUISE : ferme la séquence et désarme un appui long encore en attente.
    const onTouchEnd = (e: PointerEvent) => {
      if (e.pointerType === "mouse") return;
      touchDown.current = false;
      lastTouchEnd.current = Date.now();
      if (pendingRef.current?.pointerId === e.pointerId) clearPending();
    };
    root.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("pointerup", onTouchEnd, true);
    window.addEventListener("pointercancel", onTouchEnd, true);
    return () => {
      root.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("pointerup", onTouchEnd, true);
      window.removeEventListener("pointercancel", onTouchEnd, true);
    };
  }, [interactive, clearPending]);

  // Glisser d'évènement : suivi au niveau de la fenêtre (il traverse les colonnes) ; Échap l'annule.
  useEffect(() => {
    if (!onEventChange) return;
    const onMouseMove = (e: MouseEvent) => { const d = eventDragRef.current; if (d && d.pointerId === undefined) moveEventDrag(e.clientX, e.clientY); };
    const onMouseUp = () => { const d = eventDragRef.current; if (d && d.pointerId === undefined) endEventDrag(true); };
    const onPointerMove = (e: PointerEvent) => { const d = eventDragRef.current; if (d && d.pointerId === e.pointerId) moveEventDrag(e.clientX, e.clientY); };
    const onPointerUp = (e: PointerEvent) => { const d = eventDragRef.current; if (d && d.pointerId === e.pointerId) endEventDrag(e.type === "pointerup"); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && eventDragRef.current) { e.preventDefault(); e.stopPropagation(); endEventDrag(false); } };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [onEventChange, moveEventDrag, endEventDrag]);

  // Échap annule aussi une sélection en cours (opt-in).
  useEffect(() => {
    if (!drag || !escapeCancels) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.preventDefault(); setDragBoth(null); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [drag, escapeCancels, setDragBoth]);

  useEffect(() => clearPending, [clearPending]);

  const toPct = (minutes: number) => timeGridPercent(minutes, windowMinutes);

  const finishDrag = (colIndex: number) => {
    const d = dragRef.current;
    setDragBoth(null);
    if (!d || d.colIndex !== colIndex || !onSelectRange) return;
    const col = columns[colIndex];
    if (!col) return;
    const a = yToMinutes(Math.min(d.y0, d.y1), d.height, windowMinutes);
    const b = yToMinutes(Math.max(d.y0, d.y1), d.height, windowMinutes);
    onSelectRange({ columnKey: col.key, columnIndex: colIndex, ...snapRange(a, b, snapMinutes, windowMinutes) });
  };

  const nowInWindow = now != null && now.minute >= 0 && now.minute <= windowMinutes;
  const drawNow = renderNow ?? ((p: TimeGridNowRenderProps) => <TimeGridNowLine {...p} />);
  const gridStyle = { "--tg-columns": columns.length, "--tg-rows": graduations.length, ...style } as CSSProperties;

  const controllerFor = (colIndex: number): HTMLAttributes<HTMLDivElement> => {
    if (!interactive) return {};
    return {
      onPointerDown: e => {
        if (e.pointerType === "mouse") return; // la souris garde son glisser (onMouseDown)
        touchDown.current = true;
        const grab = onEventChange ? readGrab(e.target) : null;
        if (!grab && (e.target as Element).closest("[data-time-grid-event]")) return; // sélection = zones vides
        if (!grab && !onSelectRange) return;
        clearPending();
        const cell = e.currentTarget;
        const rect = cell.getBoundingClientRect();
        const pointerId = e.pointerId;
        const x = e.clientX;
        const yClient = e.clientY;
        const y = yClient - rect.top;
        pendingRef.current = {
          pointerId,
          colIndex,
          x,
          y: yClient,
          height: rect.height,
          top: rect.top,
          cell,
          event: grab ?? undefined,
          timer: setTimeout(() => {
            const p = pendingRef.current;
            if (!p || p.pointerId !== pointerId) return;
            pendingRef.current = null;
            try {
              p.cell.setPointerCapture?.(pointerId);
            } catch {
              // pointeur déjà relâché : le geste reste piloté par pointerup/cancel
            }
            if (p.event) beginEventDrag(p.event, x, yClient, pointerId, true);
            else setDragBoth({ colIndex, y0: y, y1: y, height: p.height, top: p.top, pointerId });
            navigator.vibrate?.(8);
          }, longPressMs),
        };
      },
      onPointerMove: e => {
        if (e.pointerType === "mouse") return;
        const p = pendingRef.current;
        if (p && p.pointerId === e.pointerId) {
          // Le doigt bouge avant l'appui long : c'est un défilement, on n'en vole rien.
          if (Math.hypot(e.clientX - p.x, e.clientY - p.y) > TIME_GRID_LONG_PRESS_SLOP_PX) clearPending();
          return;
        }
        const cur = dragRef.current;
        if (!cur || cur.pointerId !== e.pointerId) return;
        setDragBoth({ ...cur, y1: e.clientY - cur.top });
      },
      onPointerUp: e => {
        if (e.pointerType === "mouse") return;
        if (pendingRef.current?.pointerId === e.pointerId) return clearPending(); // simple tap : rien
        const cur = dragRef.current;
        if (cur && cur.pointerId === e.pointerId) finishDrag(cur.colIndex);
      },
      onPointerCancel: e => {
        if (e.pointerType === "mouse") return;
        if (pendingRef.current?.pointerId === e.pointerId) clearPending();
        if (dragRef.current?.pointerId === e.pointerId) setDragBoth(null);
      },
      onContextMenu: e => {
        // Appui long Android = menu contextuel : on le garde pour le geste en cours.
        if (pendingRef.current || dragRef.current?.pointerId !== undefined || eventDragRef.current?.pointerId !== undefined) e.preventDefault();
      },
      onMouseDown: e => {
        if (e.button !== 0) return;
        // Événements souris de compatibilité (pendant ou juste après un toucher) : le toucher a son geste.
        if (isCompatMouse()) return;
        const grab = onEventChange ? readGrab(e.target) : null;
        if (grab) { beginEventDrag(grab, e.clientX, e.clientY); return; }
        if ((e.target as Element).closest("[data-time-grid-event]")) return; // sélection = zones vides
        if (!onSelectRange) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        setDragBoth({ colIndex, y0: y, y1: y, height: rect.height, top: rect.top });
      },
      onMouseMove: e => {
        const cur = dragRef.current;
        if (!cur || cur.colIndex !== colIndex || cur.pointerId !== undefined) return;
        setDragBoth({ ...cur, y1: e.clientY - cur.top });
      },
      onMouseUp: () => {
        if (dragRef.current?.pointerId !== undefined) return; // sélection tactile : pointerup
        finishDrag(colIndex);
      },
      onMouseLeave: () => {
        const cur = dragRef.current;
        if (cur?.colIndex === colIndex && cur.pointerId === undefined) setDragBoth(null);
      },
      onClickCapture: e => {
        if (swallowClick.current) { e.preventDefault(); e.stopPropagation(); swallowClick.current = false; }
      },
    };
  };

  const hasAllDay = allDay != null;

  return (
    <div
      ref={setRoot}
      role="grid"
      aria-description={onSelectRange ? selectDescription : undefined}
      {...rest}
      className={cx(styles.grid, classNames.root, className)}
      data-time-grid=""
      style={gridStyle}
    >
      <div className={cx(styles.head, classNames.head)}>
        <div className={cx(styles.corner, classNames.corner)} aria-hidden />
        {columns.map(col => (
          <div key={col.key} className={cx(styles.colhdr, classNames.columnHeader)}>
            {col.header}
          </div>
        ))}
      </div>
      {hasAllDay ? (
        <div className={cx(styles.allDay, classNames.allDay)}>
          <div className={cx(styles.allDayLabel, classNames.allDayLabel)}>{allDay?.label}</div>
          {allDay?.content !== undefined ? (
            <div className={cx(styles.allDaySpan, classNames.allDayCell, allDay.className)}>{allDay.content}</div>
          ) : (
            columns.map(col => (
              <div key={col.key} className={cx(styles.allDayCell, classNames.allDayCell)}>
                {col.allDay}
              </div>
            ))
          )}
        </div>
      ) : null}
      <div className={cx(styles.body, classNames.body)}>
        <div className={cx(styles.gutter, classNames.gutter)} aria-hidden>
          {graduations.map(g => (
            <span key={g.key} className={cx(styles.hourlab, classNames.graduation)}>
              {g.label}
            </span>
          ))}
        </div>
        {columns.map((col, colIndex) => {
          const controller = controllerFor(colIndex);
          const own = col.cellProps ?? {};
          const merged: HTMLAttributes<HTMLDivElement> = { ...own };
          for (const [name, handler] of Object.entries(controller)) {
            (merged as Record<string, unknown>)[name] = chain((own as unknown as Record<string, (e: SyntheticEvent) => void>)[name], handler as (e: SyntheticEvent) => void);
          }
          const showNow = nowInWindow && now!.columnKeys.includes(col.key);
          return (
            <div
              key={col.key}
              role="gridcell"
              aria-label={col.label}
              {...merged}
              className={cx(styles.day, classNames.column, own.className)}
              data-tg-col={colIndex}
              // Le toucher a son geste propre (appui long) : pas d'appel de légende iOS sur la colonne.
              style={interactive ? ({ WebkitTouchCallout: "none", ...own.style } as CSSProperties) : own.style}
            >
              {graduations.map(g => (
                <div key={g.key} className={cx(styles.hourline, classNames.hourLine)} aria-hidden />
              ))}
              {col.children}
              {drag && drag.colIndex === colIndex ? (
                <div
                  className={cx(styles.select, classNames.selection)}
                  aria-hidden
                  style={{
                    top: toPct(yToMinutes(Math.min(drag.y0, drag.y1), drag.height, windowMinutes)),
                    height: toPct(
                      Math.max(
                        yToMinutes(Math.max(drag.y0, drag.y1), drag.height, windowMinutes) -
                          yToMinutes(Math.min(drag.y0, drag.y1), drag.height, windowMinutes),
                        5,
                      ),
                    ),
                  }}
                />
              ) : null}
              {preview && preview.columnIndex === colIndex ? (
                <div
                  className={cx(styles.preview, classNames.preview)}
                  aria-hidden
                  data-tg-preview={preview.kind}
                  style={{ top: toPct(preview.startMinute), height: toPct(Math.max(preview.endMinute - preview.startMinute, 5)) }}
                />
              ) : null}
              {/* Pastille d'heure UNIQUEMENT sur la colonne désignée (elle se pose dans la gouttière). */}
              {showNow
                ? drawNow({ style: { top: toPct(now!.minute) }, label: now!.labelColumnKey === col.key ? now!.label : undefined })
                : null}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export interface TimeGridNowLineProps {
  style?: CSSProperties;
  /** Heure affichée dans la pastille de gouttière. */
  label?: string;
  className?: string;
  labelClassName?: string;
}

/** Ligne « maintenant » — présentation pure : la position vient de la grille, jamais d'une horloge propre. */
export function TimeGridNowLine({ style, label, className, labelClassName }: TimeGridNowLineProps) {
  return (
    <div className={cx(styles.nowline, className)} aria-hidden style={style}>
      {label ? <span className={cx(styles.nowlabel, labelClassName)}>{label}</span> : null}
    </div>
  );
}

type EventFrameOwnProps = {
  /** Élément rendu : lien, bouton ou bloc. Défaut div. */
  as?: ElementType;
  /** Teinte de l'évènement (couleur CSS ou var()) : filet, bord gauche, fond dilué. */
  accent?: string;
  /** Identité et plage (minutes) — requis pour un évènement déplaçable. */
  eventId?: string;
  startMinute?: number;
  endMinute?: number;
  /** Déplaçable et étirable via `onEventChange` de la grille (poignée de fin rendue). */
  movable?: boolean;
  /** Libellé accessible de la poignée d'étirement (décorative par défaut : le clavier passe par l'appelant). */
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

export type TimeGridEventProps = EventFrameOwnProps & Omit<HTMLAttributes<HTMLElement>, keyof EventFrameOwnProps> & DataAttributes & { href?: string; type?: "button" | "submit" | "reset" };

/**
 * Cadre positionné commun d'un évènement de grille (top/height posés par l'appelant). Le CONTENU (titre, capacité,
 * statut…) appartient à l'appelant ; le cadre porte la peinture générique et les marqueurs du contrôleur.
 */
export const TimeGridEvent = forwardRef<HTMLElement, TimeGridEventProps>(function TimeGridEvent(
  { as: Tag = "div", accent, eventId, startMinute, endMinute, movable, className, style, children, ...rest },
  ref,
) {
  const accentStyle = accent ? ({ "--tg-event-accent": accent } as CSSProperties) : undefined;
  return (
    <Tag
      ref={ref}
      {...rest}
      className={cx(styles.event, className)}
      data-time-grid-event=""
      data-tg-id={eventId}
      data-tg-start={startMinute}
      data-tg-end={endMinute}
      data-tg-movable={movable ? "true" : undefined}
      style={{ ...style, ...accentStyle }}
    >
      {children}
      {movable ? <span className={styles.handle} data-time-grid-handle="" aria-hidden /> : null}
    </Tag>
  );
});

export default TimeGrid;
